/**
 * The Recurring tab's dialogs (TMMAPP-0101; audit 06 § C11, v2 L2405–2505): the recurring-transaction
 * editor at 560 — title "New / Edit recurring transaction", subtitle "On {envelope}", the note box,
 * Expense / Income pills, Name · Amount · Note, and the schedule box with the Start date and the inline
 * recurrence (`RecurrenceInline`, the product's one rule: the Recurring toggle, "Repeat every", weekday
 * circles, "On day N" / "On the {nth} {weekday}", the ending and "Remove recurrence") — and, kept (D1),
 * the Spread-evenly dialog, whose Name is optional. `PlanLine.name` is required on create.
 */
import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PlanLine, PlanLineKind, SpreadCadence } from '@/api/planLines';
import type { RecurrenceRule } from '@/api/planned';
import { denseFieldSx } from '@/components/denseField';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { useReturnFocus } from '@/components/useConfirm';
import { RecurrenceInline } from '@/features/planned/recurrence/RecurrenceInline';
import { emptyRule, validateRule } from '@/features/planned/recurrence/recurrence';
import { SubscribeInPlace } from '@/features/subscribe/SubscribeInPlace';
import { minorToInputText, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';
import { resolveTokens } from '@/theme/theme';
import { toCivil } from './window';

const PLAN_LINE_SAVE = 'plan-line-save';
const SPREAD_SAVE = 'spread-save';
const SUBSCRIBE_TITLE_ID = 'plan-line-subscribe-title';

/** The subscribe card in place of a plan-line dialog's form (DLG-005); Back returns to the form. */
export interface SubscribeStep {
  required: boolean;
  onCancel: () => void;
}

function SubscribeStepBody({ step, onClose }: { step: SubscribeStep; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <>
      <DialogContent>
        <SubscribeInPlace titleId={SUBSCRIBE_TITLE_ID} focusTitle onClose={onClose} />
      </DialogContent>
      <DialogActions>
        <Button onClick={step.onCancel}>{t('common.cancel')}</Button>
      </DialogActions>
    </>
  );
}

/** The host's in-place confirm while the subscribe card shows: Esc goes back to the form. */
function subscribeConfirm(step: SubscribeStep) {
  return step.required ? { titleId: SUBSCRIBE_TITLE_ID, onClose: step.onCancel } : null;
}

interface LineDialogProps {
  title: string;
  /** The envelope's name, for the "On {envelope}" subtitle. */
  envelopeName: string;
  currency: string | null;
  /** The write is in flight: Save shows its loading state and the dialog is not dismissible. */
  busy: boolean;
  /** The panel's last write error, shown inside the dialog that caused it. */
  error: string | null;
  /** Bumped when a write failed: focus moves to the error. */
  errorTick: number;
  /** The server's `name` field message (a `400 VALIDATION_FAILED`). */
  nameError: string | null;
  subscribe: SubscribeStep;
  line?: PlanLine;
  /**
   * The `409 PLAN_LINE_LIKELY_DUPLICATE` warning (TMMAPP-0173 item 8), worded by the host: the
   * dialog stays open with the person's values, Add anyway (Save anyway) resends with
   * `confirmDuplicate`, and Cancel only puts the warning away.
   */
  duplicate?: string | null | undefined;
  onDuplicateCancel?: (() => void) | undefined;
  onClose: () => void;
  onSubmit: (body: {
    name?: string;
    kind: PlanLineKind;
    amountMinor: number;
    note?: string;
    occursOn?: string;
    recurrence?: RecurrenceRule;
    confirmDuplicate?: boolean;
  }) => void;
}

/** An Expense / Income pill: radius 11, padding 6 14, 12/500; selected is the `selected` layer on a brand boundary. */
function KindPill({
  value,
  selected,
  onSelect,
}: {
  value: PlanLineKind;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <ButtonBase
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      data-kind-pill={value}
      sx={{
        borderRadius: '11px',
        p: '6px 14px',
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        color: selected ? tokens.link : tokens.text2,
        border: `1px solid ${selected ? tokens.brandBoundary : tokens.line}`,
        bgcolor: tokens.surfaceRaised,
        ...(selected
          ? { backgroundImage: `linear-gradient(${tokens.selected}, ${tokens.selected})` }
          : {}),
        '&.Mui-focusVisible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
      }}
    >
      {t(`planned.kind.${value}`)}
    </ButtonBase>
  );
}

export function PlanLineDialog({
  title,
  envelopeName,
  currency,
  busy,
  error,
  errorTick,
  nameError,
  subscribe,
  line,
  duplicate = null,
  onDuplicateCancel,
  onClose,
  onSubmit,
}: LineDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const dense = denseFieldSx(tokens);
  const today = toCivil(new Date());
  // The name field takes focus once the dialog has entered (`autoFocus` is a lint error here).
  const nameRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(line?.name ?? '');
  const [nameTouched, setNameTouched] = useState(false);
  const [kind, setKind] = useState<PlanLineKind>(line?.kind ?? 'EXPENSE');
  const [amount, setAmount] = useState(
    line !== undefined ? minorToInputText(Math.abs(line.amountMinor), currency, locale) : '',
  );
  const [note, setNote] = useState(line?.note ?? '');
  const [startOn, setStartOn] = useState(line?.recurrence.startOn ?? line?.occursOn ?? today);
  const [rule, setRule] = useState<RecurrenceRule>(line?.recurrence ?? emptyRule(today));
  const { dirty } = useDirty({ name, kind, amount, note, startOn, rule });
  const guard = useDialogDirtyGuard(dirty, onClose, busy, errorTick);
  // Back from the subscribe card, focus returns to the Save that asked (TMMAPP-0097 §C 25).
  useReturnFocus(subscribe.required, PLAN_LINE_SAVE);
  // A name is required on create and on a line that has one; a line created before names may stay
  // unnamed (TMMAPI-0143).
  const nameRequired = line === undefined || (line.name !== null && line.name.length > 0);
  const nameBlank = name.trim().length === 0;
  const recurring = rule.frequency !== 'ONCE';
  const ruleInvalid = recurring && validateRule({ ...rule, startOn }) !== null;
  const mag = parseMoneyToMinor(amount, currency, locale);
  // tmm-api takes a positive amount and reads the direction from `kind` (TMMAPI-0143).
  const sendMinor = mag === null ? null : Math.abs(mag);

  /**
   * A field changed after the 409: the warning goes and Save comes back, so tmm-api judges the new
   * values. "Add anyway" only ever resends the values it warned about (TMMAPP-0173 item 8).
   */
  function changed<T>(set: (value: T) => void) {
    return (value: T) => {
      if (duplicate !== null) onDuplicateCancel?.();
      set(value);
    };
  }

  function submit(confirmDuplicate: boolean) {
    if (sendMinor === null || (nameRequired && nameBlank)) return;
    onSubmit({
      ...(nameRequired || !nameBlank ? { name: name.trim() } : {}),
      kind,
      amountMinor: sendMinor,
      ...(note.trim().length > 0 ? { note: note.trim() } : {}),
      occursOn: startOn,
      ...(recurring ? { recurrence: { ...rule, startOn } } : {}),
      ...(confirmDuplicate ? { confirmDuplicate: true } : {}),
    });
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId="plan-line-title"
      confirm={subscribeConfirm(subscribe)}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: { sx: { width: 560, maxWidth: 'calc(100% - 32px)' } },
        transition: { onEntered: () => nameRef.current?.focus() },
      }}
    >
      {subscribe.required ? (
        <SubscribeStepBody step={subscribe} onClose={onClose} />
      ) : (
        <>
          <DialogTitle id="plan-line-title">{title}</DialogTitle>
          <DialogContent>
            <Stack sx={{ mt: 0.5, gap: 2 }}>
              <Typography
                variant="body2"
                data-dialog-subtitle=""
                sx={{ color: tokens.text2, mt: '-4px' }}
              >
                {t('planLines.editorOn', { envelope: envelopeName })}
              </Typography>
              <Box
                data-editor-note=""
                sx={{
                  backgroundColor: tokens.hover,
                  borderRadius: `${tokens.radius.control}px`,
                  p: '10px 12px',
                  fontSize: 12,
                  lineHeight: 1.6,
                  color: tokens.text2,
                }}
              >
                {t('planLines.editorNote')}
              </Box>
              {error !== null && <Alert severity="error">{error}</Alert>}
              {duplicate !== null && (
                <Alert severity="warning" data-duplicate-warning="">
                  {duplicate}
                </Alert>
              )}
              <Stack
                role="radiogroup"
                aria-label={t('planLines.kind')}
                direction="row"
                sx={{ gap: 0.75, flexWrap: 'wrap' }}
              >
                <KindPill
                  value="EXPENSE"
                  selected={kind === 'EXPENSE'}
                  onSelect={() => changed(setKind)('EXPENSE')}
                />
                <KindPill
                  value="INCOME"
                  selected={kind === 'INCOME'}
                  onSelect={() => changed(setKind)('INCOME')}
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1.25 }}>
                <TextField
                  label={t('planLines.name')}
                  value={name}
                  required={nameRequired}
                  inputRef={nameRef}
                  onChange={(event) => changed(setName)(event.target.value)}
                  onBlur={() => setNameTouched(true)}
                  error={nameError !== null || (nameTouched && nameRequired && nameBlank)}
                  helperText={
                    nameError ??
                    (nameTouched && nameRequired && nameBlank ? t('planLines.nameRequired') : null)
                  }
                  slotProps={{ htmlInput: { maxLength: 120 } }}
                  sx={{ flex: 2, ...dense }}
                />
                <TextField
                  label={t('planLines.amount')}
                  value={amount}
                  onChange={(event) => changed(setAmount)(event.target.value)}
                  slotProps={{ htmlInput: { 'aria-label': t('planLines.amount') } }}
                  sx={{ flex: 1, ...dense }}
                />
              </Stack>
              <TextField
                label={t('planLines.note')}
                value={note}
                onChange={(event) => changed(setNote)(event.target.value)}
                sx={dense}
              />
              <Box
                role="group"
                aria-label={t('planLines.schedule')}
                sx={{
                  border: `1px solid ${tokens.line}`,
                  borderRadius: `${tokens.radius.control}px`,
                  p: '14px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                <TextField
                  type="date"
                  label={t('planLines.startOn')}
                  value={startOn}
                  onChange={(event) => changed(setStartOn)(event.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ maxWidth: 180, ...dense }}
                />
                <RecurrenceInline
                  rule={{ ...rule, startOn }}
                  onChange={(next) => changed(setRule)({ ...next, startOn })}
                  startOn={startOn}
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            {duplicate !== null ? (
              <>
                <Button onClick={() => onDuplicateCancel?.()} disabled={busy}>
                  {t('common.cancel')}
                </Button>
                <SubmitButton
                  data-confirm-opener={PLAN_LINE_SAVE}
                  loading={busy}
                  onClick={() => submit(true)}
                >
                  {line === undefined ? t('planLines.addAnyway') : t('planLines.saveAnyway')}
                </SubmitButton>
              </>
            ) : (
              <>
                <Button onClick={guard.requestClose} disabled={busy}>
                  {t('common.cancel')}
                </Button>
                <SubmitButton
                  data-confirm-opener={PLAN_LINE_SAVE}
                  loading={busy}
                  disabled={
                    sendMinor === null ||
                    (nameRequired && nameBlank) ||
                    startOn === '' ||
                    ruleInvalid
                  }
                  onClick={() => submit(false)}
                >
                  {t('common.save')}
                </SubmitButton>
              </>
            )}
          </DialogActions>
        </>
      )}
    </GuardedDialog>
  );
}

interface SpreadDialogProps {
  currency: string | null;
  /** The write is in flight: Save shows its loading state and the dialog is not dismissible. */
  busy: boolean;
  /** The panel's last write error, shown inside the dialog that caused it. */
  error: string | null;
  /** Bumped when a write failed: focus moves to the error. */
  errorTick: number;
  subscribe: SubscribeStep;
  onClose: () => void;
  onSubmit: (body: {
    name?: string;
    totalMinor: number;
    startOn: string;
    endOn: string;
    cadence: SpreadCadence;
    note?: string;
  }) => void;
}

export function SpreadDialog({
  currency,
  busy,
  error,
  errorTick,
  subscribe,
  onClose,
  onSubmit,
}: SpreadDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const today = toCivil(new Date());
  const [name, setName] = useState('');
  const [total, setTotal] = useState('');
  const [startOn, setStartOn] = useState(today);
  const [endOn, setEndOn] = useState(today);
  const [cadence, setCadence] = useState<SpreadCadence>('MONTHLY');
  const [note, setNote] = useState('');
  const { dirty } = useDirty({ name, total, startOn, endOn, cadence, note });
  const guard = useDialogDirtyGuard(dirty, onClose, busy, errorTick);
  useReturnFocus(subscribe.required, SPREAD_SAVE);
  const mag = parseMoneyToMinor(total, currency, locale);
  const sendMinor = mag === null ? null : Math.abs(mag);

  return (
    <GuardedDialog
      guard={guard}
      titleId="spread-title"
      confirm={subscribeConfirm(subscribe)}
      fullWidth
      maxWidth="sm"
    >
      {subscribe.required ? (
        <SubscribeStepBody step={subscribe} onClose={onClose} />
      ) : (
        <>
          <DialogTitle id="spread-title">{t('planLines.spreadTitle')}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              {error !== null && <Alert severity="error">{error}</Alert>}
              <Typography variant="body2">{t('planLines.spreadHelp')}</Typography>
              <TextField
                label={t('planLines.spreadName')}
                value={name}
                onChange={(event) => setName(event.target.value)}
                slotProps={{ htmlInput: { maxLength: 120 } }}
              />
              <TextField
                label={t('planLines.total')}
                value={total}
                onChange={(event) => setTotal(event.target.value)}
                slotProps={{ htmlInput: { 'aria-label': t('planLines.total') } }}
              />
              <TextField
                type="date"
                label={t('planLines.startOn')}
                value={startOn}
                onChange={(event) => setStartOn(event.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                type="date"
                label={t('planLines.endOn')}
                value={endOn}
                onChange={(event) => setEndOn(event.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <FormControl fullWidth>
                <InputLabel id="spread-cadence">{t('planLines.cadence')}</InputLabel>
                <Select
                  labelId="spread-cadence"
                  label={t('planLines.cadence')}
                  value={cadence}
                  onChange={(event) => setCadence(event.target.value as SpreadCadence)}
                >
                  <MenuItem value="MONTHLY">{t('planLines.cadenceMonthly')}</MenuItem>
                  <MenuItem value="WEEKLY">{t('planLines.cadenceWeekly')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label={t('planLines.note')}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={guard.requestClose} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <SubmitButton
              data-confirm-opener={SPREAD_SAVE}
              loading={busy}
              disabled={sendMinor === null || startOn > endOn}
              onClick={() => {
                if (sendMinor === null) return;
                onSubmit({
                  ...(name.trim().length > 0 ? { name: name.trim() } : {}),
                  totalMinor: sendMinor,
                  startOn,
                  endOn,
                  cadence,
                  ...(note.trim().length > 0 ? { note: note.trim() } : {}),
                });
              }}
            >
              {t('common.save')}
            </SubmitButton>
          </DialogActions>
        </>
      )}
    </GuardedDialog>
  );
}
